import React, { useCallback, useRef, useState } from 'react';
import { Alert } from 'react-native';
import { useTranslation } from 'react-i18next';

import GPSContractModal from '../components/GPS/GPSContractModal';
import {
  acceptCompanyGpsContract,
  getCompanyLicenses,
  getGpsContractText,
  type GpsContractDocument,
} from '../api/apiService';

export interface GpsContractGatePlan {
  price_id: string;
  product?: { metadata?: { feature?: string } | null } | null;
  metadata?: { feature?: string } | null;
}

export type GpsContractGateOutcome = 'proceed' | 'shown' | 'load_error';

export interface GpsContractGateHandlers {
  /** Runs after the contract shown was accepted; the modal stays up until it resolves (no flash). */
  onAccepted: () => Promise<void> | void;
  /** The owner closed the contract, or recording the acceptance failed. Nothing was paid. */
  onCancelled?: (reason: 'rejected' | 'failed') => void;
}

interface PendingPurchase {
  plan: GpsContractGatePlan;
  quantity: number;
  handlers: GpsContractGateHandlers;
}

const loadContract = async (plan: GpsContractGatePlan, quantity: number): Promise<GpsContractDocument | null> => {
  const textResp = await getGpsContractText(plan.price_id, quantity);
  if (!textResp.ok || !textResp.data?.version_id || !textResp.data?.text) return null;
  return textResp.data;
};

export const isGpsOrComboPlan = (plan: GpsContractGatePlan): boolean => {
  const feature = plan.product?.metadata?.feature || plan.metadata?.feature;
  return feature === 'gps' || feature === 'combo';
};

/**
 * D-059 / ALL-125: the single GPS contract step shared by every GPS/Combo purchase surface
 * (Gerir Licenças, owner onboarding, expired paywall). New customers must read and accept
 * the published contract for this exact price and quantity before any payment starts.
 */
export function useGpsContractGate() {
  const { t } = useTranslation('onboarding');
  const [visible, setVisible] = useState(false);
  const [contract, setContract] = useState<GpsContractDocument | null>(null);
  const [accepting, setAccepting] = useState(false);
  const pending = useRef<PendingPurchase | null>(null);

  const loadErrorMessage = t('gpsContractLoadError', 'Não foi possível carregar o contrato GPS. Tente novamente.');

  const reset = useCallback(() => {
    setVisible(false);
    setContract(null);
    pending.current = null;
  }, []);

  const requestContract = useCallback(async (
    plan: GpsContractGatePlan,
    quantity: number,
    handlers: GpsContractGateHandlers,
  ): Promise<GpsContractGateOutcome> => {
    if (!isGpsOrComboPlan(plan)) return 'proceed';
    try {
      const licensesResp = await getCompanyLicenses();
      const summary = licensesResp.ok ? licensesResp.data?.summary : undefined;
      // D-059: the backend says whether this company still has to accept (new customers only).
      const contractRequired = typeof summary?.gps_contract_required === 'boolean'
        ? summary.gps_contract_required
        : !summary?.gps_contract_accepted_at;
      if (!contractRequired) return 'proceed';
      const loaded = await loadContract(plan, quantity);
      if (!loaded) return 'load_error';
      pending.current = { plan, quantity, handlers };
      setContract(loaded);
      setVisible(true);
      return 'shown';
    } catch (error) {
      console.error('GPS contract gate failed to load:', error);
      return 'load_error';
    }
  }, []);

  const handleAccept = async () => {
    const purchase = pending.current;
    if (!purchase || !contract) return;
    setAccepting(true);
    try {
      // Send back exactly the version that was displayed; the backend refuses any other.
      const resp = await acceptCompanyGpsContract({
        version_id: contract.version_id,
        price_id: purchase.plan.price_id,
        quantity: purchase.quantity,
      });
      if (!resp.ok && resp.status === 409) {
        // The published version changed while the modal was open: show the new one, do not pay.
        const refreshed = await loadContract(purchase.plan, purchase.quantity);
        if (!refreshed) throw new Error('GPS contract refresh failed');
        setContract(refreshed);
        Alert.alert(
          t('gpsContractVersionChangedTitle', 'O contrato foi atualizado'),
          t('gpsContractVersionChangedMessage', 'Foi publicada uma nova versão do contrato GPS. Leia a versão apresentada antes de aceitar.'),
        );
        return;
      }
      if (!resp.ok) {
        throw new Error('GPS contract acceptance failed');
      }

      // Keep the contract modal visible while the payment step is prepared so the
      // previous surface does not flash; clean up in the same render cycle after.
      await purchase.handlers.onAccepted();
      reset();
    } catch (e) {
      console.error('Accept GPS contract failed:', e);
      reset();
      Alert.alert(t('gpsContractAcceptErrorTitle'), t('gpsContractAcceptErrorMessage'));
      purchase.handlers.onCancelled?.('failed');
    } finally {
      setAccepting(false);
    }
  };

  const handleReject = () => {
    const purchase = pending.current;
    reset();
    purchase?.handlers.onCancelled?.('rejected');
  };

  const contractModal = (
    <GPSContractModal
      visible={visible}
      onAccept={handleAccept}
      onReject={handleReject}
      accepting={accepting}
      contractSummary={contract?.summary || undefined}
      contractText={contract?.text}
      versionId={contract?.version_id}
      versionDate={contract?.version_date}
      ownerEmail={contract?.owner_email}
    />
  );

  return { requestContract, contractModal, visible, reset, loadErrorMessage };
}
